/**
 * areas.ts — struktura obszarów produktu na ekranie logowania (showcase).
 *
 * Trzyma WYŁĄCZNIE dane strukturalne (id, ikona, gradient, klucze modułów). Cała treść
 * user-facing (tytuł, hasło, opis obszaru + nazwy/opisy modułów) jest w i18n pod
 * `public.showcase.*` — patrz `apps/admin/src/i18n/{pl,en}.json`. Statyczna
 * (pre-login, brak dostępu do API modułów). Kolejność: „Obieg Dokumentów" pierwszy.
 *
 * `id`          = klucz i18n obszaru (`public.showcase.areas.<id>`).
 * `moduleKeys`  = klucze i18n modułów (`public.showcase.areas.<id>.modules.<key>`),
 *                 kolejność tablicy = kolejność renderowania listy w modalu.
 * `Icon`        = komponent lucide-react.
 * `gradient`    = klasy Tailwind `bg-gradient-to-br` (reużyte w kafelku, jego
 *                 hover-washu, nagłówku modala i kropkach listy modułów).
 */

import {
  BarChart3,
  FileStack,
  FolderKanban,
  Landmark,
  ListChecks,
  type LucideIcon,
  Megaphone,
  MessagesSquare,
  ShoppingCart,
  Users,
  Wrench,
} from 'lucide-react';

export interface LoginArea {
  id: string;
  Icon: LucideIcon;
  gradient: string;
  moduleKeys: string[];
}

export const LOGIN_AREAS: LoginArea[] = [
  {
    id: 'obieg-dokumentow',
    Icon: FileStack,
    gradient: 'from-sky-500 to-blue-600',
    moduleKeys: [
      'faktury-zakupowe',
      'skaner-ocr',
      'ksef',
      'rejestry-vat',
      'workflow-akceptacji',
      'dokumenty-notatki',
    ],
  },
  {
    id: 'finanse-ksiegowosc',
    Icon: Landmark,
    gradient: 'from-emerald-500 to-teal-600',
    moduleKeys: [
      'eksport-fk',
      'plan-kont-mpk',
      'budzety',
      'bankowosc',
      'kursy-walut',
      'szablony-dekretacji',
    ],
  },
  {
    id: 'kadry-place',
    Icon: Users,
    gradient: 'from-violet-500 to-purple-600',
    moduleKeys: [
      'pracownicy',
      'rcp-czas-pracy',
      'dokumenty-pracownicze',
      'certyfikaty-uprawnienia',
      'wnioski',
      'karty-drogowe',
    ],
  },
  {
    id: 'serwis-reklamacje',
    Icon: Wrench,
    gradient: 'from-orange-500 to-amber-600',
    moduleKeys: [
      'zgloszenia-serwisowe',
      'reklamacje',
      'helpdesk-tickety',
      'wizyty-harmonogram',
      'przeglady-hvac-fgaz',
    ],
  },
  {
    id: 'sprzedaz-magazyn',
    Icon: ShoppingCart,
    gradient: 'from-rose-500 to-pink-600',
    moduleKeys: ['crm', 'oferty', 'sklep-b2b', 'wms-magazyn', 'zamowienia-prognozy'],
  },
  {
    id: 'kontroling-raporty',
    Icon: BarChart3,
    gradient: 'from-indigo-500 to-blue-600',
    moduleKeys: ['dashboardy', 'raporty', 'analityka-sprzedazy', 'asystent-ai'],
  },
  {
    id: 'komunikacja',
    Icon: MessagesSquare,
    gradient: 'from-cyan-500 to-sky-600',
    moduleKeys: ['poczta', 'czat-zespolowy', 'live-chat', 'telefonia', 'powiadomienia'],
  },
  {
    id: 'marketing',
    Icon: Megaphone,
    gradient: 'from-fuchsia-500 to-purple-600',
    moduleKeys: ['social-media', 'kampanie-email', 'formularze-landing', 'widget-callback'],
  },
  {
    id: 'projekty',
    Icon: FolderKanban,
    gradient: 'from-teal-500 to-emerald-600',
    moduleKeys: ['projekty', 'kanban', 'harmonogram-gantt', 'zasoby-czas'],
  },
  {
    id: 'zadania',
    Icon: ListChecks,
    gradient: 'from-slate-500 to-gray-600',
    moduleKeys: ['zadania', 'kalendarz', 'automatyzacje', 'przypomnienia'],
  },
];
